/**
 * Shader core: parameterized "ink flow + grain" background.
 * Every visual constant is a uniform so the same shader powers both the hero
 * (fixed defaults) and the shader lab (live-tuned). Colors are stored as hex
 * strings in params for easy editing/export and converted to vec3 at draw time.
 */

export const DEFAULT_PARAMS = {
  // motion
  speed: 0.3,
  scale: 2.2,
  warp: 2.7,
  octaves: 4,
  // ink palette
  color1: '#042339',
  color2: '#063842',
  color3: '#04191c',
  inkLow: 0.36,
  inkHigh: 0.56,
  c2: 0.85,
  // gold bloom
  goldColor: '#e0b347',
  goldX: 0.7,
  goldY: 0.34,
  goldRadius: 0.65,
  goldBase: 0.13,
  goldFlicker: 0.1,
  goldDriftAmp: 0.09,
  goldDriftSpeed: 0.85,
  // grain & vignette
  grain: 0.014,
  grainScale: 1,
  vignette: 0.81
};

const HERO_OVERRIDE_KEY = 'heroShaderParams';

/** Hero params = defaults merged with any lab "apply to hero" override (this browser). */
export function loadHeroParams() {
  try {
    const raw = localStorage.getItem(HERO_OVERRIDE_KEY);
    if (raw) return { ...DEFAULT_PARAMS, ...JSON.parse(raw) };
  } catch (e) { /* ignore */ }
  return { ...DEFAULT_PARAMS };
}

export function saveHeroParams(params) {
  try { localStorage.setItem(HERO_OVERRIDE_KEY, JSON.stringify(params)); } catch (e) {}
}

export function clearHeroParams() {
  try { localStorage.removeItem(HERO_OVERRIDE_KEY); } catch (e) {}
}

function hexToRgb(hex) {
  const h = (hex || '#000000').replace('#', '');
  const n = h.length === 3
    ? h.split('').map(c => c + c).join('')
    : h.padEnd(6, '0').slice(0, 6);
  return [
    parseInt(n.slice(0, 2), 16) / 255,
    parseInt(n.slice(2, 4), 16) / 255,
    parseInt(n.slice(4, 6), 16) / 255
  ];
}

const VERT = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

const FRAG = `
precision highp float;
uniform vec2 u_res;
uniform float u_time;
uniform float u_speed, u_scale, u_warp, u_inkLow, u_inkHigh, u_c2;
uniform vec3 u_color1, u_color2, u_color3, u_goldColor;
uniform float u_goldX, u_goldY, u_goldRadius, u_goldBase, u_goldFlicker, u_goldDriftAmp, u_goldDriftSpeed;
uniform float u_grain, u_grainScale, u_vignette;
uniform int u_octaves;

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 345.45));
  p += dot(p, p + 34.345);
  return fract(p.x * p.y);
}
float noise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0; float a = 0.5;
  for (int i = 0; i < 6; i++) {
    if (i >= u_octaves) break;
    v += a * noise(p); p *= 2.0; a *= 0.5;
  }
  return v;
}
void main() {
  vec2 uv = gl_FragCoord.xy / u_res.xy;
  float aspect = u_res.x / u_res.y;
  vec2 p = vec2(uv.x * aspect, uv.y) * u_scale;
  float t = u_time * u_speed;

  vec2 q = vec2(
    fbm(p + vec2(0.0, t)),
    fbm(p + vec2(5.2, 1.3) - vec2(t * 0.8, 0.0))
  );
  float f = fbm(p + u_warp * q);

  vec3 col = mix(u_color3, u_color1, smoothstep(u_inkLow, u_inkHigh, f));
  col = mix(col, u_color2, smoothstep(0.55, 0.95, q.x) * u_c2);

  vec2 gp = vec2(
    u_goldX + u_goldDriftAmp * sin(t * u_goldDriftSpeed),
    u_goldY + u_goldDriftAmp * cos(t * u_goldDriftSpeed * 0.85)
  );
  gp.x *= aspect;
  float gd = distance(vec2(uv.x * aspect, uv.y), gp);
  float glow = smoothstep(u_goldRadius, 0.0, gd);
  col += u_goldColor * glow * (u_goldBase + u_goldFlicker * f);

  float grain = hash(gl_FragCoord.xy * u_grainScale + fract(u_time) * 97.0);
  col += (grain - 0.5) * u_grain;

  float vig = smoothstep(1.25, 0.4, length(uv - 0.5));
  col *= mix(u_vignette, 1.0, vig);

  gl_FragColor = vec4(col, 1.0);
}
`;

/**
 * Create the ink shader on a canvas.
 * @param {HTMLCanvasElement} canvas
 * @param {object} opts
 *   getParams()   -> current params object (read every frame; supports live tuning)
 *   reduce        -> if true, draw one static frame and don't loop
 *   maxWidth      -> cap backing-buffer width (default 1280)
 *   renderScale   -> fraction of CSS size to render at (default 0.7; use 1 for the lab)
 *   fps           -> frame cap (default 30)
 *   autoObserve   -> pause when canvas scrolls offscreen (default true)
 * @returns {{ play, stop, resize, drawOnce, destroy } | null}
 */
export function createInkShader(canvas, opts = {}) {
  const {
    getParams = () => DEFAULT_PARAMS,
    reduce = false,
    maxWidth = 1280,
    renderScale = 0.7,
    fps = 30,
    autoObserve = true
  } = opts;

  let gl;
  try {
    gl = canvas.getContext('webgl', {
      antialias: false, alpha: false, depth: false, stencil: false, powerPreference: 'low-power'
    });
  } catch (e) { gl = null; }
  if (!gl) { canvas.remove(); return null; }

  const compile = (type, src) => {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.error('Shader compile error:', gl.getShaderInfoLog(s));
      return null;
    }
    return s;
  };
  const vs = compile(gl.VERTEX_SHADER, VERT);
  const fs = compile(gl.FRAGMENT_SHADER, FRAG);
  if (!vs || !fs) { canvas.remove(); return null; }

  const prog = gl.createProgram();
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    console.error('Shader link error:', gl.getProgramInfoLog(prog));
    canvas.remove();
    return null;
  }
  gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const aPos = gl.getAttribLocation(prog, 'a_pos');
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  const U = name => gl.getUniformLocation(prog, name);
  const u = {
    res: U('u_res'), time: U('u_time'), speed: U('u_speed'), scale: U('u_scale'),
    warp: U('u_warp'), inkLow: U('u_inkLow'), inkHigh: U('u_inkHigh'), c2: U('u_c2'),
    color1: U('u_color1'), color2: U('u_color2'), color3: U('u_color3'), goldColor: U('u_goldColor'),
    goldX: U('u_goldX'), goldY: U('u_goldY'), goldRadius: U('u_goldRadius'), goldBase: U('u_goldBase'),
    goldFlicker: U('u_goldFlicker'), goldDriftAmp: U('u_goldDriftAmp'), goldDriftSpeed: U('u_goldDriftSpeed'),
    grain: U('u_grain'), grainScale: U('u_grainScale'), vignette: U('u_vignette'), octaves: U('u_octaves')
  };

  function applyParams() {
    const p = getParams();
    gl.uniform1f(u.speed, p.speed);
    gl.uniform1f(u.scale, p.scale);
    gl.uniform1f(u.warp, p.warp);
    gl.uniform1f(u.inkLow, p.inkLow);
    gl.uniform1f(u.inkHigh, p.inkHigh);
    gl.uniform1f(u.c2, p.c2);
    gl.uniform3fv(u.color1, hexToRgb(p.color1));
    gl.uniform3fv(u.color2, hexToRgb(p.color2));
    gl.uniform3fv(u.color3, hexToRgb(p.color3));
    gl.uniform3fv(u.goldColor, hexToRgb(p.goldColor));
    gl.uniform1f(u.goldX, p.goldX);
    gl.uniform1f(u.goldY, p.goldY);
    gl.uniform1f(u.goldRadius, p.goldRadius);
    gl.uniform1f(u.goldBase, p.goldBase);
    gl.uniform1f(u.goldFlicker, p.goldFlicker);
    gl.uniform1f(u.goldDriftAmp, p.goldDriftAmp);
    gl.uniform1f(u.goldDriftSpeed, p.goldDriftSpeed);
    gl.uniform1f(u.grain, p.grain);
    gl.uniform1f(u.grainScale, p.grainScale);
    gl.uniform1f(u.vignette, p.vignette);
    gl.uniform1i(u.octaves, Math.round(p.octaves));
  }

  function resize() {
    const w = canvas.clientWidth || window.innerWidth;
    const h = canvas.clientHeight || window.innerHeight;
    const s = Math.min(1, maxWidth / Math.max(1, w)) * renderScale;
    const bw = Math.max(1, Math.round(w * s));
    const bh = Math.max(1, Math.round(h * s));
    if (canvas.width !== bw || canvas.height !== bh) { canvas.width = bw; canvas.height = bh; }
    gl.viewport(0, 0, bw, bh);
    gl.uniform2f(u.res, bw, bh);
    if (reduce || !running) drawOnce();
  }

  let raf = 0, running = false, startTs = null, lastTs = 0;
  const FRAME_MS = 1000 / fps;

  function drawOnce(timeSeconds) {
    applyParams();
    gl.uniform1f(u.time, timeSeconds != null ? timeSeconds : 9.0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  function frame(now) {
    if (!running) return;
    raf = requestAnimationFrame(frame);
    if (now - lastTs < FRAME_MS) return;
    lastTs = now;
    if (startTs === null) startTs = now;
    applyParams();
    gl.uniform1f(u.time, (now - startTs) / 1000);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  function play() { if (running || reduce) return; running = true; lastTs = 0; raf = requestAnimationFrame(frame); }
  function stop() { running = false; if (raf) cancelAnimationFrame(raf); raf = 0; }

  resize();
  window.addEventListener('resize', resize, { passive: true });

  if (reduce) {
    drawOnce();
    return { play() {}, stop() {}, resize, drawOnce, destroy() {} };
  }

  if (autoObserve && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(e => { e[0].isIntersecting ? play() : stop(); }, { threshold: 0 });
    io.observe(canvas);
  }
  document.addEventListener('visibilitychange', () => { document.hidden ? stop() : play(); });
  play();

  return {
    play, stop, resize, drawOnce,
    destroy() { stop(); window.removeEventListener('resize', resize); }
  };
}
